/**
 * Subscription Store
 *
 * Manages subscription state and feature gating for the app.
 */

import { computed, reactive, ref } from "vue"
import { apiRequest, type Unsubscribe } from "../services/apiClient"
import type { Subscription, SubscriptionPlan, SubscriptionTier, TierFeatures } from "../types/subscription"
import { SUBSCRIPTION_PLANS, TIER_FEATURES } from "../types/subscription"
import { applyFocusMode } from "../utils/focusMode"
import { logger } from "../utils/logger"
import { isAnonymous } from "./authStore"

// Default subscription for free users
const DEFAULT_SUBSCRIPTION: Subscription = {
  tier: "free",
  status: "active",
  provider: "vipps",
  currentPeriodStart: new Date().toISOString(),
  currentPeriodEnd: new Date(Date.now() + 100 * 365 * 24 * 60 * 60 * 1000).toISOString(),
  cancelAtPeriodEnd: false,
  priceNOK: 0,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
}

interface SubscriptionState {
  subscription: Subscription
  isLoading: boolean
  isInitialized: boolean
  error: string | null
}

const state = reactive<SubscriptionState>({
  subscription: { ...DEFAULT_SUBSCRIPTION },
  isLoading: false,
  isInitialized: false,
  error: null,
})

// Billing is disabled by default: there is no company behind the app, so it
// cannot legally charge users, and the in-app Vipps flow would be rejected by
// App Store review anyway (Apple 3.1.1 requires IAP for digital features).
// Set VITE_BILLING_ENABLED=true to bring the paywall UI back.
//
// With billing disabled every feature unlocks for every user EXCEPT the two
// that spend real money or ride on a per-company registration, which stay
// tier-gated (grant a tier directly in the subscriptions table instead):
// - bankSync: the Enable Banking registration is personal to the owner
// - aiMonthlyQuota: AI calls bill to the owner's API keys (server-enforced)
export const isBillingEnabled = import.meta.env.VITE_BILLING_ENABLED === "true"

const FREE_MODE_UNLOCKS: Partial<TierFeatures> = {
  maxAccounts: -1,
  maxTransactionsPerMonth: -1,
  householdMembers: -1,
  aiReceiptScanning: true,
  pdfExport: true,
  csvExport: true,
  recurringTransactions: true,
  savingsGoals: true,
  investments: true,
}

const unsubscribeRef = ref<Unsubscribe | null>(null)
let currentUserId: string | null = null
let initInFlight: Promise<void> | null = null

/**
 * Initialize subscription listener for a user.
 * Fetches current status from the API, then subscribes to Supabase Realtime
 * changes on the user's subscription row to refetch on every change.
 */
export function initializeSubscription(userId: string): Promise<void> {
  if (currentUserId === userId && state.isInitialized) return Promise.resolve()
  // Concurrent duplicate calls (auth listener + view mount) must share one
  // init — a second pass would open a second Realtime channel and leak the
  // first, since unsubscribeRef only remembers the last one.
  if (initInFlight && currentUserId === userId) return initInFlight

  cleanupSubscription()
  currentUserId = userId
  state.isLoading = true
  state.error = null

  initInFlight = (async () => {
    try {
      const refetch = async () => {
        state.subscription = await apiRequest<Subscription>("/api/subscriptions/status")
      }
      await refetch()
      // Superseded while the fetch was in flight (logout or another user's
      // init) — registering the poll now would leak it past cleanup.
      if (currentUserId !== userId) return
      // Realtime is retired (phase 3) — a slow poll keeps the tier fresh
      // (e.g. an admin-granted upgrade); Vipps flows refetch explicitly.
      const timer = setInterval(() => {
        refetch().catch((error) => logger.warn("Subscription refetch failed", { error: String(error) }))
      }, 300_000)
      unsubscribeRef.value = () => {
        clearInterval(timer)
      }
      state.isInitialized = true
    } catch (error) {
      logger.error("Failed to initialize subscription", error)
      state.subscription = { ...DEFAULT_SUBSCRIPTION }
      state.error = error instanceof Error ? error.message : String(error)
      // Deliberately NOT marked initialized: the guard above would otherwise
      // block every retry, locking a paying user who launched offline to
      // free-tier features for the whole session.
    } finally {
      state.isLoading = false
      initInFlight = null
    }
  })()
  return initInFlight
}

// Retry a failed init when connectivity returns (a failed fetch left no
// Realtime channel behind, so nothing else would ever refetch the tier).
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (currentUserId && !state.isInitialized && !state.isLoading) {
      void initializeSubscription(currentUserId)
    }
  })
}

export function cleanupSubscription(): void {
  if (unsubscribeRef.value) {
    unsubscribeRef.value()
    unsubscribeRef.value = null
  }
  currentUserId = null
  state.isInitialized = false
}

export function resetSubscription(): void {
  cleanupSubscription()
  state.subscription = { ...DEFAULT_SUBSCRIPTION }
  state.isLoading = false
  state.error = null
}

// Computed getters
export const subscription = computed(() => state.subscription)
export const subscriptionTier = computed(() => state.subscription.tier)
export const subscriptionStatus = computed(() => state.subscription.status)
export const isSubscriptionLoading = computed(() => state.isLoading)
export const subscriptionError = computed(() => state.error)

export const currentFeatures = computed((): TierFeatures => {
  const base = TIER_FEATURES[state.subscription.tier]
  return applyFocusMode(isBillingEnabled ? base : { ...base, ...FREE_MODE_UNLOCKS })
})

export const isSubscriptionActive = computed((): boolean => {
  if (state.subscription.status !== "active") return false
  const now = new Date()
  const periodEnd = new Date(state.subscription.currentPeriodEnd)
  return now < periodEnd
})

export const isExpiringSoon = computed((): boolean => {
  if (!isSubscriptionActive.value) return false
  if (!state.subscription.cancelAtPeriodEnd) return false
  const now = new Date()
  const periodEnd = new Date(state.subscription.currentPeriodEnd)
  const sevenDays = 7 * 24 * 60 * 60 * 1000
  return periodEnd.getTime() - now.getTime() < sevenDays
})

export const daysRemaining = computed((): number => {
  const now = new Date()
  const periodEnd = new Date(state.subscription.currentPeriodEnd)
  const diffMs = periodEnd.getTime() - now.getTime()
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)))
})

// Feature check helpers.
// AI features additionally require a real (non-anonymous) account: they burn
// a metered server budget, and the API rejects anonymous sessions with
// `account_required`. Guests get an account-linking CTA instead (see
// AssistantView / InsightsView).
export const canUseAI = computed(() => !isAnonymous.value && currentFeatures.value.aiInsights)
export const canUseAIAssistant = computed(() => !isAnonymous.value && currentFeatures.value.aiAssistant)
export const canUseReceiptScanning = computed(() => !isAnonymous.value && currentFeatures.value.aiReceiptScanning)
export const aiMonthlyQuota = computed(() => currentFeatures.value.aiMonthlyQuota)
export const canExportPDF = computed(() => currentFeatures.value.pdfExport)
export const canExportCSV = computed(() => currentFeatures.value.csvExport)
export const canUseRecurring = computed(() => currentFeatures.value.recurringTransactions)
export const canUseSavingsGoals = computed(() => currentFeatures.value.savingsGoals)
export const canUseInvestments = computed(() => currentFeatures.value.investments)
export const canUseBank = computed(() => currentFeatures.value.bankSync)
export const maxAccounts = computed(() => currentFeatures.value.maxAccounts)
export const maxTransactionsPerMonth = computed(() => currentFeatures.value.maxTransactionsPerMonth)
export const maxHouseholdMembers = computed(() => currentFeatures.value.householdMembers)

export function canAddAccount(currentCount: number): boolean {
  const limit = currentFeatures.value.maxAccounts
  return limit === -1 || currentCount < limit
}

export function canAddTransaction(currentMonthCount: number): boolean {
  const limit = currentFeatures.value.maxTransactionsPerMonth
  return limit === -1 || currentMonthCount < limit
}

export function canAddHouseholdMember(currentMemberCount: number): boolean {
  const limit = currentFeatures.value.householdMembers
  return limit === -1 || currentMemberCount < limit
}

export function getPlan(tier: SubscriptionTier): SubscriptionPlan {
  return SUBSCRIPTION_PLANS[tier]
}

export function getAllPlans(): SubscriptionPlan[] {
  return Object.values(SUBSCRIPTION_PLANS)
}

// Billing actions are money: a double-tap while the network round-trips must
// not create two Vipps agreements or send two cancels. One action in flight
// at a time; re-entrant calls get the same promise.
let billingActionInFlight: Promise<unknown> | null = null

function runBillingAction<T>(action: () => Promise<T>): Promise<T> {
  if (billingActionInFlight) return billingActionInFlight as Promise<T>
  const promise = action().finally(() => {
    billingActionInFlight = null
  })
  billingActionInFlight = promise
  return promise
}

const billingPending = ref(false)
/** True while an upgrade/cancel/change call is in flight — disable buttons on it. */
export const isBillingActionPending = computed(() => billingPending.value)

/**
 * Upgrade subscription — returns the Vipps confirmation URL to redirect to.
 */
export function upgradeSubscription(tier: "plus" | "pro", phoneNumber?: string): Promise<string> {
  return runBillingAction(async () => {
    billingPending.value = true
    try {
      const result = await apiRequest<{ agreementUrl: string }>("/api/subscriptions/agreement", {
        method: "POST",
        body: { tier, phoneNumber },
      })
      return result.agreementUrl
    } finally {
      billingPending.value = false
    }
  })
}

/**
 * Cancel subscription (at period end).
 */
export function cancelSubscription(): Promise<void> {
  return runBillingAction(async () => {
    billingPending.value = true
    try {
      await apiRequest<{ success: boolean }>("/api/subscriptions/cancel", { method: "POST" })
    } finally {
      billingPending.value = false
    }
  })
}

/**
 * Change subscription tier — returns the new Vipps confirmation URL.
 */
export function changeSubscription(newTier: "plus" | "pro", phoneNumber?: string): Promise<string> {
  return runBillingAction(async () => {
    billingPending.value = true
    try {
      const result = await apiRequest<{ agreementUrl: string }>("/api/subscriptions/change", {
        method: "POST",
        body: { newTier, phoneNumber },
      })
      return result.agreementUrl
    } finally {
      billingPending.value = false
    }
  })
}

/**
 * Refresh subscription status from Vipps.
 */
export async function refreshSubscriptionStatus(): Promise<void> {
  state.subscription = await apiRequest<Subscription>("/api/subscriptions/status")
}

// Export state for testing
export function getSubscriptionState() {
  return state
}
